import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { decode, encodeAll } from '@aytm/app/insights/dashboard/lib/sanitize'
import { MAX_SUBJECT_TITLE_LENGTH } from '@aytm/app/insights/dashboard/lib/config'

import { InputTextarea } from 'primereact/inputtextarea'

import Overlay from '../../../Overlay'

import './Edit.css'

const CLASS = 'insights-portal-subject-edit'

const Edit = props => {
  const editor = useMemo(() => props.editor, [props.editor])
  const text = useMemo(() => editor?.data?.text || '', [editor?.data?.text])
  const subject = useMemo(() => props.subject, [props.subject])
  const subjects = useMemo(() => props.subjects, [props.subjects])
  const subjectType = useMemo(() => editor?.data?.type, [editor?.data?.type])
  const names = useMemo(
    () =>
      Object.values(subjects || {})
        .filter(v => subject.uid !== v.uid)
        .reduce(
          (acc, cur) =>
            acc.concat(
              decode(cur?.text?.toString() || '')
                .trim()
                .toLowerCase()
            ),
          []
        ),
    [subject.uid, subjects]
  )
  const duplicateName = useMemo(() => names.includes(text.trim().toLowerCase()), [names, text])

  const invalid = useMemo(() => !text.trim() || duplicateName, [duplicateName, text])

  const show = useMemo(
    () => props.index === editor?.itemIndex && 'rename' === editor?.type,
    [editor?.itemIndex, editor?.type, props.index]
  )

  const onTextChange = event => {
    const newText = event.target.value
    if (props.onEditorChange) {
      const data = editor?.data || {}
      props.onEditorChange({ action: 'change', data: { ...data, text: encodeAll(newText) } })
    }
  }

  const onTextCancel = () => {
    if (props.onEditorChange) {
      props.onEditorChange({})
    }
  }

  const onTextReset = () => {
    if (props.onEditorChange) {
      const data = editor?.data || {}
      props.onEditorChange({ action: 'change', data: { ...data, text: '' } })
      props.onEditorChange({ action: 'save' })
    }
  }

  const onTextSave = () => {
    if (!invalid && props.onEditorChange) {
      props.onEditorChange({ action: 'save' })
    }
  }

  const onKeyDown = event => {
    if (props.onEditorChange) {
      if ('Escape' === event.key) {
        props.onEditorChange({})
      } else if ('Enter' === event.key) {
        event.preventDefault()
        onTextSave()
      }
    }
  }

  const onOuterClick = () => {
    if (props.onEditorChange) {
      props.onEditorChange({})
    }
  }

  const resetComponent =
    'wave' === subjectType ? (
      <div className={`${CLASS}-controls-button`} onClick={onTextReset}>
        Reset name
      </div>
    ) : (
      <div></div>
    )

  const errorComponent = duplicateName ? <div className={`${CLASS}-controls-error`}>Choose a unique name</div> : null

  return editor ? (
    <Overlay show={show} className={CLASS} onKeyDown={onKeyDown} onOuterClick={onOuterClick}>
      <div>Rename:</div>
      <InputTextarea
        className={`insights-portal ${CLASS}-text${invalid ? ' p-invalid' : ''}`}
        autoResize
        rows={1}
        value={decode(text)}
        maxLength={MAX_SUBJECT_TITLE_LENGTH}
        onChange={onTextChange}
      />
      {errorComponent}
      <div className={`${CLASS}-controls`}>
        {resetComponent}
        <div className={`${CLASS}-controls-right`}>
          <div className={`${CLASS}-controls-button`} onClick={onTextCancel}>
            Cancel
          </div>
          <div
            className={`${CLASS}-controls-button${invalid ? ` ${CLASS}-controls-button-invalid` : ''}`}
            onClick={onTextSave}
          >
            Save
          </div>
        </div>
      </div>
    </Overlay>
  ) : null
}

Edit.propTypes = {
  className: PropTypes.string,
  index: PropTypes.number,
  editor: PropTypes.object,
  subject: PropTypes.object,
  subjects: PropTypes.object,
  onEditorChange: PropTypes.func
}

export default Edit
